import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProviderService } from '@/services/provider.service';
import { Provider, ProviderTypeLabels } from '@/models/provider.model';
import { Button, Card, Badge, LoadingSpinner, EmptyState } from '@/components/common';
import { Building2, ArrowLeft, Edit, Mail, Phone, Globe, MapPin, Star, CheckCircle2, Heart, HeartOff, StarOff, Calendar, DollarSign, TrendingUp, Activity } from 'lucide-react';

export const ProviderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'financial' | 'subscription' | 'activity'>('overview');

  useEffect(() => {
    if (!id) return;

    const loadProvider = async () => {
      try {
        const data = await ProviderService.getProviderById(id);
        setProvider(data);
      } catch (error) {
        console.error('Error loading provider:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProvider();
  }, [id]);

  const handleToggleSubscription = async () => {
    if (!provider) return;
    try {
      await ProviderService.toggleSubscription(provider.id, provider.isSubscribed);
      // Reload provider
      const updated = await ProviderService.getProviderById(provider.id);
      setProvider(updated);
    } catch (error) {
      console.error('Error toggling subscription:', error);
      alert('Failed to update subscription status');
    }
  };

  const handleTogglePreferred = async () => {
    if (!provider) return;
    try {
      await ProviderService.togglePreferred(provider.id, provider.isFairCarePreferred);
      // Reload provider
      const updated = await ProviderService.getProviderById(provider.id);
      setProvider(updated);
    } catch (error) {
      console.error('Error toggling preferred status:', error);
      alert('Failed to update preferred status');
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return <LoadingSpinner text="Loading provider details..." fullScreen />;
  }

  if (!provider) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={Building2}
          title="Provider not found"
          description="The provider you're looking for doesn't exist."
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/providers')}>
              Back to Providers
            </Button>
          }
        />
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: Building2 },
    { id: 'financial' as const, label: 'Financial', icon: DollarSign },
    { id: 'subscription' as const, label: 'Subscription', icon: TrendingUp },
    { id: 'activity' as const, label: 'Activity', icon: Activity },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/providers')}
          className="mb-4"
        >
          Back to Providers
        </Button>
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${
                provider.isSubscribed 
                  ? 'bg-gradient-to-br from-green-100 to-green-200' 
                  : 'bg-gradient-to-br from-blue-100 to-blue-200'
              }`}>
                <Building2 className={`w-8 h-8 ${provider.isSubscribed ? 'text-green-600' : 'text-blue-600'}`} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{provider.name}</h1>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge variant="info" size="sm">{ProviderTypeLabels[provider.type]}</Badge>
                  {provider.isFairCarePreferred && (
                    <Badge variant="warning" size="sm">
                      <Star className="w-3 h-3 mr-1" />
                      Preferred Provider
                    </Badge>
                  )}
                  {provider.isSubscribed && (
                    <Badge variant="success" size="sm">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Subscribed
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant={provider.isSubscribed ? "danger" : "success"}
                size="sm"
                icon={provider.isSubscribed ? HeartOff : Heart}
                onClick={handleToggleSubscription}
                gradient={false}
              >
                {provider.isSubscribed ? 'Unsubscribe' : 'Subscribe'}
              </Button>
              <Button
                variant={provider.isFairCarePreferred ? "warning" : "outline"}
                size="sm"
                icon={provider.isFairCarePreferred ? StarOff : Star}
                onClick={handleTogglePreferred}
                gradient={false}
              >
                {provider.isFairCarePreferred ? 'Remove Preferred' : 'Mark Preferred'}
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={Edit}
                onClick={() => navigate(`/providers/${provider.id}/edit`)}
              >
                Edit
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Card padding="none">
        <div className="border-b border-gray-200 bg-gray-50/50">
          <nav className="flex -mb-px px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && <OverviewTab provider={provider} formatDate={formatDate} />}
          {activeTab === 'financial' && <FinancialTab provider={provider} formatDate={formatDate} formatCurrency={formatCurrency} />}
          {activeTab === 'subscription' && <SubscriptionTab provider={provider} formatDate={formatDate} />}
          {activeTab === 'activity' && <ActivityTab provider={provider} formatDate={formatDate} />}
        </div>
      </Card>
    </div>
  );
};

interface TabProps {
  provider: Provider;
  formatDate: (date: Date) => string;
  formatCurrency?: (amount?: number) => string;
}

const OverviewTab: React.FC<TabProps> = ({ provider, formatDate }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Contact Information
          </h3>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Email</dt>
              <dd className="text-sm text-gray-900 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                {provider.email || 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Phone</dt>
              <dd className="text-sm text-gray-900 flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                {provider.phoneNumber || 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Website</dt>
              <dd className="text-sm text-gray-900">
                {provider.website ? (
                  <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {provider.website}
                  </a>
                ) : 'N/A'}
              </dd>
            </div>
          </dl>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Location
          </h3>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Address</dt>
              <dd className="text-sm text-gray-900">
                {provider.address ? (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      {provider.address}
                      {provider.city && provider.state && (
                        <><br />{provider.city}, {provider.state} {provider.zipCode || ''}</>
                      )}
                    </div>
                  </div>
                ) : 'N/A'}
              </dd>
            </div>
            {provider.latitude && provider.longitude && (
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Coordinates</dt>
                <dd className="text-sm text-gray-900 font-mono">
                  {provider.latitude.toFixed(6)}, {provider.longitude.toFixed(6)}
                </dd>
              </div>
            )}
          </dl>
        </Card>
      </div>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Specialties</h3>
        {provider.specialties.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {provider.specialties.map((specialty, idx) => (
              <Badge key={idx} variant="info" size="md">
                {specialty}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No specialties listed</p>
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Reviews & Ratings
        </h3>
        {provider.rating ? (
          <div className="flex items-center gap-4">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-gray-900">{provider.rating.toFixed(1)}</span>
              <span className="text-gray-500">/ 5.0</span>
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-5 h-5 ${
                    star <= Math.round(provider.rating!) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            {provider.totalReviews && (
              <span className="text-sm text-gray-500">({provider.totalReviews} reviews)</span>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No ratings available</p>
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
          <dl className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <dt className="text-sm font-medium text-gray-500">Subscribed</dt>
              <Badge variant={provider.isSubscribed ? "success" : "gray"} size="sm">
                {provider.isSubscribed ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <dt className="text-sm font-medium text-gray-500">FairCare Preferred</dt>
              <Badge variant={provider.isFairCarePreferred ? "warning" : "gray"} size="sm">
                {provider.isFairCarePreferred ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div className="flex items-center justify-between py-2">
              <dt className="text-sm font-medium text-gray-500">Accepts Cash</dt>
              <Badge variant={provider.acceptsCash ? "success" : "gray"} size="sm">
                {provider.acceptsCash ? 'Yes' : 'No'}
              </Badge>
            </div>
          </dl>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Metadata
          </h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Created</dt>
              <dd className="text-sm text-gray-900">{formatDate(provider.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Last Updated</dt>
              <dd className="text-sm text-gray-900">{formatDate(provider.lastUpdated)}</dd>
            </div>
            {provider.userId && (
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">User ID</dt>
                <dd className="text-sm text-gray-900 font-mono">{provider.userId}</dd>
              </div>
            )}
          </dl>
        </Card>
      </div>
    </div>
  );
};

const FinancialTab: React.FC<TabProps> = ({ provider, formatCurrency }) => {
  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <dt className="text-sm font-medium text-blue-600 mb-1">Average Cash Price</dt>
            <dd className="text-3xl font-bold text-blue-900">
              {formatCurrency!(provider.averageCashPrice)}
            </dd>
          </div>
          <DollarSign className="w-12 h-12 text-blue-400 opacity-50" />
        </div>
      </Card>

      {provider.servicePrices && Object.keys(provider.servicePrices).length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            Service Prices
          </h3>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(provider.servicePrices).map(([service, price]) => (
                  <tr key={service} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {service}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                      {formatCurrency!(price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {provider.stripeAccountId && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500 mb-1">Stripe Account ID</dt>
              <dd className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded">{provider.stripeAccountId}</dd>
            </div>
          </dl>
        </Card>
      )}
    </div>
  );
};

const SubscriptionTab: React.FC<TabProps> = ({ provider, formatDate }) => {
  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Subscription Status
          </h3>
          <Badge variant={provider.isSubscribed ? "success" : "gray"} size="md">
            {provider.isSubscribed ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        <dl className="space-y-4">
          {provider.subscriptionTier && (
            <div className="py-3 border-b border-gray-100">
              <dt className="text-sm font-medium text-gray-500 mb-1">Subscription Tier</dt>
              <dd className="text-sm text-gray-900 capitalize font-medium">{provider.subscriptionTier}</dd>
            </div>
          )}
          {provider.subscriptionExpiresAt && (
            <div className="py-3">
              <dt className="text-sm font-medium text-gray-500 mb-1">Expires At</dt>
              <dd className="text-sm text-gray-900">{formatDate(provider.subscriptionExpiresAt)}</dd>
            </div>
          )}
          {!provider.subscriptionTier && !provider.subscriptionExpiresAt && (
            <p className="text-sm text-gray-500">No subscription information available</p>
          )}
        </dl>
      </Card>
    </div>
  );
};

const ActivityTab: React.FC<TabProps> = ({ provider, formatDate }) => {
  return (
    <div className="space-y-6">
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          Recent Activity
        </h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4 pb-4 border-b border-gray-100">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Provider Created</p>
              <p className="text-sm text-gray-500 mt-1">{formatDate(provider.createdAt)}</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                <Activity className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Last Updated</p>
              <p className="text-sm text-gray-500 mt-1">{formatDate(provider.lastUpdated)}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

